import { useState, useEffect, useRef, useCallback } from "react";
import { useFetch, useFetchRun } from "@/Hooks/useFetch";
import { Button } from "./Naver";
export const BASE_URL = import.meta.env.VITE_API_URL;
import type { BaseResponse } from "@models/base";

interface SignupFormData {
  email: string;
  phone: string;
  name: string;
  blog_url: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
  marketing_accepted: boolean;
}

interface Props {
  ncode: string | null;
  onErrorChange: (hasError: boolean) => void;
  onBack: () => void;
}

export default function NaverSignup({ ncode, onBack, onErrorChange }: Props) {
  const naverProfile = useFetch<
    BaseResponse<{
      email?: string;
      mobile?: string;
      name?: string;
      profile_image?: string;
    }>
  >({
    url: `${BASE_URL}/user/naver/profile?ncode=${ncode}`,
    method: "GET",
  });

  const [formData, setFormData] = useState<SignupFormData>({
    email: "",
    phone: "",
    name: "",
    blog_url: "",
    terms_accepted: false,
    privacy_accepted: false,
    marketing_accepted: false,
  });

  useEffect(() => {
    if (naverProfile.fetchData?.data) {
      setFormData((prev) => ({
        ...prev,
        email: naverProfile.fetchData?.data.email || "",
        phone: naverProfile.fetchData?.data.mobile || "",
        name: naverProfile.fetchData?.data.name || "",
      }));
      validateEmail(naverProfile.fetchData?.data.email || "");
    }
  }, [naverProfile.fetchData]);

  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const signupHook = useFetchRun<BaseResponse<string>>({
    url: `${BASE_URL}/user/naver/signup`,
    method: "POST",
    body: { ...formData, ncode },
  });

  const emailValidation = useFetchRun<BaseResponse<string>>({
    url: `${BASE_URL}/user/email-validation`,
    method: "get",
  });

  const validateEmail = useCallback(
    (email: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setEmailError(null);

      debounceTimerRef.current = setTimeout(async () => {
        if (email && email.includes("@")) {
          try {
            const response = await emailValidation.executeFetch({
              url: `${BASE_URL}/user/email-validation?email=${email}`,
            });
            if (response?.meta?.code === 400) {
              setEmailError(
                "이미 가입된 이메일 주소입니다. 다른 이메일 또는 기존 아이디 연동을 진행해주세요."
              );
            } else if (response?.meta?.code === 200) {
              setEmailError(null);
            }
          } catch (error) {
            console.error("Email validation error:", error);
          }
        }
      }, 500);
    },
    [emailValidation]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (name === "email") {
      validateEmail(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    onErrorChange(false);
    if (!formData.terms_accepted || !formData.privacy_accepted) {
      setError("필수 약관에 동의해주세요.");
      onErrorChange(true);
      return;
    }

    try {
      const response = await signupHook.executeFetch();
      if (response?.meta?.code === 200) {
        alert("회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.");
        window.location.href = "/login";
      } else {
        if (response.meta.code == 409) {
          setError(
            "이미 가입된 이메일 주소입니다. 다른이메일또는 기존아이디 연동을 진행해주세요."
          );
        } else {
          setError(
            response?.meta?.message || "회원가입 중 오류가 발생했습니다."
          );
          onErrorChange(true);
        }
      }
    } catch (err) {
      setError("서버 연결에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.");
      onErrorChange(true);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        <input
          type="hidden"
          name="profile_image"
          value={naverProfile.fetchData?.data?.profile_image || ""}
        />
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            이메일
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all duration-200 ${
              emailError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-[#03C75A]"
            }`}
            placeholder="이메일을 입력하세요"
            required
          />
          {emailError && (
            <p className="mt-1 text-xs text-red-600">{emailError}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            휴대전화
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#03C75A] focus:border-transparent outline-none transition-all duration-200"
            placeholder="휴대전화 번호를 입력하세요"
            required
          />
        </div>

        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            이름
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#03C75A] focus:border-transparent outline-none transition-all duration-200"
            placeholder="이름을 입력하세요"
            required
          />
        </div>

        <div>
          <label
            htmlFor="blog_url"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            블로그 URL
          </label>
          <input
            type="url"
            id="blog_url"
            name="blog_url"
            value={formData.blog_url}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#03C75A] focus:border-transparent outline-none transition-all duration-200"
            placeholder="블로그 URL을 입력하세요"
          />
        </div>

        {/* 약관 동의 섹션 */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="terms_accepted"
              name="terms_accepted"
              checked={formData.terms_accepted}
              onChange={handleChange}
              className="w-4 h-4 text-[#03C75A] border-gray-300 rounded focus:ring-[#03C75A]"
            />
            <label htmlFor="terms_accepted" className="text-sm text-gray-700">
              이용약관에 동의 (필수)
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="privacy_accepted"
              name="privacy_accepted"
              checked={formData.privacy_accepted}
              onChange={handleChange}
              className="w-4 h-4 text-[#03C75A] border-gray-300 rounded focus:ring-[#03C75A]"
            />
            <label htmlFor="privacy_accepted" className="text-sm text-gray-700">
              개인정보 수집 동의 (필수)
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="marketing_accepted"
              name="marketing_accepted"
              checked={formData.marketing_accepted}
              onChange={handleChange}
              className="w-4 h-4 text-[#03C75A] border-gray-300 rounded focus:ring-[#03C75A]"
            />
            <label
              htmlFor="marketing_accepted"
              className="text-sm text-gray-700"
            >
              마케팅 활용 동의 (선택)
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
          {error}
        </div>
      )}

      <div className="pt-4">
        <Button type="submit" variant="primary" loading={signupHook.loading}>
          가입하기
        </Button>
      </div>
      <div className="flex items-center mb-6">
        <Button type="button" variant="secondary" onClick={onBack}>
          뒤로가기
        </Button>
      </div>
    </form>
  );
}
